import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { usePlan } from '../../context/PlanContext';

interface TrainerBranding {
  business_name: string;
  logo_url: string;
}

export default function TrainerBrandingSettings() {
  const { planTier } = usePlan();
  const [branding, setBranding] = useState<TrainerBranding>({
    business_name: '',
    logo_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');

  // Check if user has access to custom branding
  const hasCustomBrandingAccess = planTier === 'early_supporter';

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('trainer_branding')
        .select('business_name, logo_url')
        .eq('trainer_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching branding:', error);
      } else if (data) {
        setBranding(data);
        if (data.logo_url) {
          setLogoPreview(data.logo_url);
        }
      }
    } catch (error) {
      console.error('Error fetching branding:', error);
    } finally {
      setLoading(false);
    }
  };

  const uploadLogo = async (file: File): Promise<string> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}/logo.${fileExt}`;

    const { data, error } = await supabase.storage
      .from('trainer-logos')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: true
      });

    if (error) throw error;

    const { data: { publicUrl } } = supabase.storage
      .from('trainer-logos')
      .getPublicUrl(fileName);

    return publicUrl;
  };

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      let logoUrl = branding.logo_url;

      // Upload new logo if selected
      if (logoFile) {
        logoUrl = await uploadLogo(logoFile);
      }

      // Use upsert with proper conflict resolution
      const { error } = await supabase
        .from('trainer_branding')
        .upsert({
          trainer_id: user.id,
          business_name: branding.business_name,
          logo_url: logoUrl
        }, {
          onConflict: 'trainer_id'
        });

      if (error) throw error;

      setBranding(prev => ({ ...prev, logo_url: logoUrl }));
      setLogoFile(null);
      alert('Branding settings saved successfully! 🎨');
    } catch (error) {
      console.error('Error saving branding:', error);
      alert('Error saving branding settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview('');
    setBranding(prev => ({ ...prev, logo_url: '' }));
  };

  if (loading) {
    return (
      <div className="retro-card p-4">
        <div className="index-tab">
          <h3 className="text-lg font-semibold text-[#1f1f1f]">🎨 Branding Settings</h3>
        </div>
        <div className="p-4">
          <div className="text-center py-4 text-[#2d2d2d]">Loading branding settings...</div>
        </div>
      </div>
    );
  }

  // Show upgrade prompt if user doesn't have access
  if (!hasCustomBrandingAccess) {
    return (
      <div className="retro-card p-4">
        <div className="index-tab">
          <h3 className="text-lg font-semibold text-[#1f1f1f]">🎨 Branding Settings</h3>
        </div>
        <div className="p-4">
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 text-center">
            <div className="text-4xl mb-4">🎨</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Custom Branding</h3>
            <p className="text-gray-600 mb-4">
              Add your business name and logo to custom reports. This feature is available exclusively to Early Supporters.
            </p>
            <div className="bg-blue-50 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-blue-900 mb-2">Early Supporter Benefits:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Custom branded reports (logo + business name)</li>
                <li>• 30 client limit (vs 5 current)</li>
                <li>• Priority support and early access</li>
                <li>• Price lock for 12 months</li>
              </ul>
            </div>
            <button
              onClick={() => window.location.href = '/subscription'}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-semibold"
            >
              Upgrade to Early Supporter
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="retro-card p-4">
      <div className="index-tab">
        <h3 className="text-lg font-semibold text-[#1f1f1f]">🎨 Branding Settings</h3>
      </div>
      <div className="p-4 space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Business Name
          </label>
          <input
            type="text"
            value={branding.business_name}
            onChange={(e) => setBranding(prev => ({ ...prev, business_name: e.target.value }))}
            placeholder="Your Business/Gym Name"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            This name will appear on your custom reports instead of "MacTrack"
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Business Logo
          </label>
          <div className="space-y-4">
            {logoPreview && (
              <div className="flex items-center space-x-4">
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  className="w-16 h-16 object-contain border border-gray-300 rounded"
                />
                <button
                  onClick={handleRemoveLogo}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  Remove Logo
                </button>
              </div>
            )}
            
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500">
              Upload your business logo (PNG, JPG, SVG). Recommended size: 200x200px
            </p>
          </div>
        </div>

        <div className="bg-blue-50 rounded-lg p-4">
          <h4 className="font-semibold text-blue-900 mb-2">Preview</h4>
          <div className="flex items-center space-x-3">
            {logoPreview ? (
              <img
                src={logoPreview}
                alt="Logo"
                className="w-8 h-8 object-contain"
              />
            ) : (
              <div className="w-8 h-8 bg-gray-200 rounded flex items-center justify-center text-gray-500 text-xs">
                MM
              </div>
            )}
            <span className="font-bold text-gray-900">
              {branding.business_name || 'MacTrack'}
            </span>
          </div>
          <p className="text-sm text-blue-700 mt-2">
            This is how your branding will appear on custom reports
          </p>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {saving ? 'Saving...' : 'Save Branding Settings'}
        </button>
      </div>
    </div>
  );
}
